// Criterios de "día apto" de la obra (los elige el cliente). Se guardan por cámara en el backend (climaConfigs).
import React, { useEffect, useState } from 'react';
import { X, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import R from './reglasClima';
import { guardarConfig } from './configClima';

const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const GRUPOS = [
  { titulo: '🌧️ Lluvia', campos: [['lluviaNoApto', 'No apto desde', 'mm en el horario', 0.5], ['lluviaCuidado', 'Con cuidado desde', 'mm', 0.5], ['probCuidado', 'Con cuidado si la probabilidad pasa', '%', 5]] },
  { titulo: '💨 Viento', campos: [['rafagaNoApto', 'No apto con ráfagas de', 'km/h', 5], ['rafagaCuidado', 'Con cuidado desde', 'km/h', 5], ['gruaRafaga', 'Grúa: no izar con ráfagas de', 'km/h', 5]] },
  { titulo: '🌡️ Temperatura', campos: [['calorNoApto', 'Calor extremo desde', '°C', 1], ['calorCuidado', 'Calor con cuidado desde', '°C', 1], ['frioNoApto', 'Helada: no apto con', '°C o menos', 1], ['frioCuidado', 'Frío con cuidado con', '°C o menos', 1]] },
  { titulo: '🚛 Hormigonado', campos: [['hormigonTmin', 'Temperatura mínima', '°C', 1], ['hormigonTmax', 'Temperatura máxima', '°C', 1], ['hormigonRafaga', 'Ráfagas hasta', 'km/h', 5], ['ventanaAntes', 'Sin lluvia antes', 'horas', 6], ['ventanaDespues', 'Sin lluvia después', 'horas', 6]] },
];

export default function Umbrales({ open, onClose, feedId, U, onGuardado }) {
  const [u, setU] = useState(U);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { if (open) setU(U); }, [open, U]);
  if (!open) return null;
  const set = (k, v) => setU((x) => ({ ...x, [k]: v }));
  const guardar = async () => {
    setGuardando(true);
    try {
      const limpio = R.sanearUmbrales(u);
      const c = await guardarConfig(feedId, { umbrales: limpio });
      onGuardado(c);
      toast.success('Listo: los criterios quedaron guardados para esta obra.');
      onClose();
    } catch (e) { toast.error(e.message); }
    setGuardando(false);
  };
  const inp = 'w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500';
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Criterios de día apto">
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-gray-100">
          <div>
            <p className="font-semibold text-gray-900">¿Cuándo un día es apto para trabajar?</p>
            <p className="text-xs text-gray-500 mt-0.5">Ajustalo a tu obra. Se usa en el semáforo, el planificador, el recomendador, los avisos y el informe PDF.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Cerrar"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-5">
          <div>
            <p className="text-sm font-semibold text-gray-800 mb-2">🕗 Horario y días de trabajo</p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm text-gray-700 flex items-center gap-2">De
                <select value={u.jornadaDesde} onChange={(e) => set('jornadaDesde', +e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1 text-sm">{Array.from({ length: 14 }, (_, i) => i + 4).map((h) => <option key={h} value={h}>{h} h</option>)}</select>
              </label>
              <label className="text-sm text-gray-700 flex items-center gap-2">a
                <select value={u.jornadaHasta} onChange={(e) => set('jornadaHasta', +e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1 text-sm">{Array.from({ length: 14 }, (_, i) => i + 11).map((h) => <option key={h} value={h}>{h} h</option>)}</select>
              </label>
              <div className="flex gap-1">
                {DIAS.map((d, i) => {
                  const on = u.diasLaborales.includes(i + 1);
                  return <button key={i} type="button" aria-pressed={on} onClick={() => set('diasLaborales', on ? u.diasLaborales.filter((x) => x !== i + 1) : [...u.diasLaborales, i + 1].sort())} className={`w-8 h-8 rounded-full text-xs font-semibold border ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}>{d}</button>;
                })}
              </div>
            </div>
          </div>
          {GRUPOS.map((g) => (
            <div key={g.titulo}>
              <p className="text-sm font-semibold text-gray-800 mb-2">{g.titulo}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {g.campos.map(([k, label, unidad, paso]) => (
                  <label key={k} className="flex items-center justify-between gap-3 text-sm text-gray-700">
                    <span className="min-w-0">{label}</span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      <input type="number" step={paso} min={R.LIMITES[k][0]} max={R.LIMITES[k][1]} value={u[k]} onChange={(e) => set(k, e.target.value === '' ? '' : Number(e.target.value))} className={inp} aria-label={label} />
                      <span className="text-xs text-gray-500 w-16">{unidad}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100">
          <button type="button" onClick={() => setU(R.UMBRALES_DEFAULT)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 mr-auto"><RotateCcw size={14} />Valores recomendados</button>
          <button type="button" onClick={onClose} className="text-sm font-semibold border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">Cancelar</button>
          <button type="button" onClick={guardar} disabled={guardando} className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">{guardando && <Loader2 size={14} className="animate-spin" />}Guardar</button>
        </div>
      </div>
    </div>
  );
}
